import { useParams } from 'react-router-dom';
import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import { useGetCodelistForEntryQuery } from 'features/codelist/data/useGetCodelistForEntry';
import { CodelistRecord } from 'features/codelist/domain/codelistRecordSchema';
import { Button } from 'features/components/Button';
import { useCreateCodelistRecordMutation } from 'features/codelist/data/useCreateCodelistRecordMutation';
import { useUpdateCodelistForEntryMutation } from 'features/codelist/data/useUpdateCodelistForEntryMutation';
import { useAttachCodelistRecordToEntryMutation } from 'features/codelist/data/useAttachCodelistRecordToEntryMutation';
import clsx from 'clsx';
import { CloseIcon, DeleteIcon } from '@icons';
import { IconButton } from 'features/entry-form-v2/component/IconButton';
import { useState } from 'react';
import { CodelistDisplay } from './display/CodelistDisplay';
import { useGetProjectQuery } from 'features/entry-form-v2/data/useGetProjectQuery';
import { Pencil, Plus, Search } from 'lucide-react';
import { SearchCodelistModal } from 'features/codelist/search/SearchCodelistModal';
import { toast } from 'react-toastify';
import { useDetachCodelistRecordFromEntryMutation } from '../data/useDetachCodelistRecordFromEntryMutation';
import { LedidiRequestError } from 'features/entry-form-v2/data/makeRequest';
import { CodelistForm } from './form/CodelistForm';
import { useGetLatestDataEntryVersionQuery } from 'features/entry-form-v2/data/useGetLatestDataEntryVersionQuery';
import { Skeleton } from 'features/entry-form-v2/component/Skeleton';
import { CodelistHistoryModalButton } from './history/CodelistHistoryModalButton';
import { Loader } from 'components/UI/Loader';
import { ConfirmDetachCodelistModal } from './ConfirmDetachCodelistModal';

export const CodelistSidebar = ({
	entryId,
	open,
	onClose
}: {
	entryId: string;
	open: boolean;
	onClose: () => void;
}) => {
	const params = useParams();
	const projectId = params.projectId as string;

	const language = useLanguage();
	const translation = DICTIONARY[language];

	const dataEntryQuery = useGetLatestDataEntryVersionQuery({
		entryId,
		projectId
	});

	const projectQuery = useGetProjectQuery(
		{ projectId: projectId ?? '' },
		{ enabled: !!projectId }
	);

	const hasCodelistWriteAccess =
		projectQuery.data?.project.userAccess?.accessCodelistWrite ?? false;

	const originalEntryId = dataEntryQuery.data?.entry?.original_id;
	const codelistRecordId = dataEntryQuery.data?.entry?.codelist_record_id;

	return (
		<div
			className={clsx(
				'fixed top-[60px] right-0 bottom-0 bg-gray-100 z-50 w-[300px] flex flex-col transition-transform duration-100 ease-in',
				{
					'translate-x-[300px]': !open,
					'translate-x-0': open
				}
			)}
		>
			<div className="flex justify-between items-center px-10 pt-10 pb-6 shrink-0">
				<div className="flex flex-row gap-2 items-center">
					{codelistRecordId && (
						<CodelistHistoryModalButton
							originalEntryId={originalEntryId}
							codelistRecordId={codelistRecordId}
						/>
					)}

					<h2 className="text-lg font-bold">{translation.title}</h2>
				</div>

				<IconButton onClick={onClose}>
					<CloseIcon className="size-6" />
				</IconButton>
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto px-10 pb-10">
				{dataEntryQuery.isLoading ? (
					<LoadingView />
				) : codelistRecordId ? (
					<DisplayCodelistView
						originalEntryId={originalEntryId}
						projectId={projectId}
						codelistRecordId={codelistRecordId}
						hasCodelistWriteAccess={hasCodelistWriteAccess}
					/>
				) : (
					<EmptyCodelistView
						originalEntryId={originalEntryId}
						projectId={projectId}
						hasCodelistWriteAccess={hasCodelistWriteAccess}
					/>
				)}
			</div>
		</div>
	);
};

const EmptyCodelistView = ({
	originalEntryId,
	projectId,
	hasCodelistWriteAccess
}: {
	originalEntryId: string;
	projectId: string;
	hasCodelistWriteAccess: boolean;
}) => {
	const [activeView, setActiveView] = useState<'selection' | 'create'>('selection');
	const [selectedRecord, setSelectedRecord] = useState<CodelistRecord & { id: string }>();
	const [showSearchModal, setShowSearchModal] = useState(false);

	return (
		<div>
			{selectedRecord ? (
				<SelectedRecordView
					record={selectedRecord}
					onBack={() => setSelectedRecord(undefined)}
					originalEntryId={originalEntryId}
					projectId={projectId}
				/>
			) : (
				<CodelistSelectActionFlow
					activeView={activeView}
					setActiveView={setActiveView}
					originalEntryId={originalEntryId}
					projectId={projectId}
					hasCodelistWriteAccess={hasCodelistWriteAccess}
					onOpenSearchModal={() => setShowSearchModal(true)}
				/>
			)}

			<SearchCodelistModal
				projectId={projectId}
				open={showSearchModal}
				onClose={() => setShowSearchModal(false)}
				onRecordClick={record => {
					setSelectedRecord(record);
					setShowSearchModal(false);
				}}
			/>
		</div>
	);
};

const DisplayCodelistView = ({
	originalEntryId,
	projectId,
	codelistRecordId,
	hasCodelistWriteAccess
}: {
	originalEntryId: string;
	projectId: string;
	codelistRecordId: string;
	hasCodelistWriteAccess: boolean;
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [showConfirmDetachModal, setShowConfirmDetachModal] = useState(false);

	const getCodelistForEntryQuery = useGetCodelistForEntryQuery(
		{ entryId: originalEntryId, projectId: projectId },
		{ enabled: !!originalEntryId }
	);

	const detachCodelistMutation = useDetachCodelistRecordFromEntryMutation({
		originalEntryId: originalEntryId,
		projectId: projectId,
		codelistRecordId: codelistRecordId
	});

	const codelist =
		getCodelistForEntryQuery.data?.type === 'success'
			? getCodelistForEntryQuery.data.record
			: undefined;

	if (getCodelistForEntryQuery.isLoading || !getCodelistForEntryQuery.data) {
		return <LoadingView />;
	}

	if (getCodelistForEntryQuery.data?.type === 'error') {
		return <CodelistSidebarError error={getCodelistForEntryQuery.data.error} />;
	}

	if (isEditing) {
		return (
			<UpdateCodelistForm
				originalEntryId={originalEntryId}
				projectId={projectId}
				initialCodelist={getCodelistForEntryQuery.data.record}
				onCancelClicked={() => setIsEditing(false)}
			/>
		);
	}

	if (!hasCodelistWriteAccess) {
		return <CodelistDisplay codelist={codelist} />;
	}

	return (
		<div>
			<CodelistDisplay codelist={codelist} />

			<div className="flex flex-col gap-4">
				<EditButton
					onClick={() => setIsEditing(true)}
					isDisabled={detachCodelistMutation.isLoading}
				/>
				<DeleteButton
					onClick={() => setShowConfirmDetachModal(true)}
					isLoading={detachCodelistMutation.isLoading}
				/>
			</div>

			<ConfirmDetachCodelistModal
				open={showConfirmDetachModal}
				onClose={() => setShowConfirmDetachModal(false)}
				detachMutation={detachCodelistMutation}
			/>
		</div>
	);
};

const UpdateCodelistForm = ({
	originalEntryId,
	projectId,
	initialCodelist,
	onCancelClicked
}: {
	originalEntryId: string;
	projectId: string;
	initialCodelist: CodelistRecord;
	onCancelClicked: () => void;
}) => {
	const updateCodelistForEntryMutation = useUpdateCodelistForEntryMutation({
		originalEntryId: originalEntryId,
		projectId: projectId
	});

	return (
		<CodelistForm
			onSubmit={async data => {
				await updateCodelistForEntryMutation.mutateAsync(data);
				onCancelClicked();
			}}
			initialCodelist={initialCodelist}
			onCancelClicked={onCancelClicked}
			loading={updateCodelistForEntryMutation.isLoading}
		/>
	);
};

const CreateCodelistForm = ({
	originalEntryId,
	projectId,
	onCancelClicked
}: {
	originalEntryId: string;
	projectId: string;
	onCancelClicked: () => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];
	const [error, setError] = useState<string>();

	const createCodelistForEntryMutation = useCreateCodelistRecordMutation({
		originalEntryId: originalEntryId,
		projectId: projectId
	});

	const submit = async (data: CodelistRecord) => {
		try {
			await createCodelistForEntryMutation.mutateAsync(data);
		} catch (error: unknown) {
			if (error instanceof LedidiRequestError) {
				if (error.errors.find(e => e.code === 'error.alreadyExists')) {
					setError(translation.createCodelistForm.errors.alreadyExists);
					return;
				}
			}

			setError(translation.createCodelistForm.errors.generic);
		}
	};

	return (
		<CodelistForm
			onSubmit={submit}
			onCancelClicked={onCancelClicked}
			loading={createCodelistForEntryMutation.isLoading}
			error={error}
		/>
	);
};

const EditButton = ({ onClick, isDisabled }: { onClick: () => void; isDisabled: boolean }) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<button
			onClick={onClick}
			disabled={isDisabled}
			type="button"
			className={clsx(
				'text-sm rounded-md font-semibold flex items-center justify-center gap-4 mt-6 hover:bg-gray-200 p-4 w-full border border-gray-900',
				{ 'cursor-not-allowed': isDisabled }
			)}
		>
			<Pencil className="size-6" />
			<span>{translation.editButton}</span>
		</button>
	);
};

const DeleteButton = ({ onClick, isLoading }: { onClick: () => void; isLoading: boolean }) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	return (
		<button
			onClick={onClick}
			disabled={isLoading}
			type="button"
			className={clsx(
				'text-sm rounded-md font-semibold flex items-center justify-center gap-4 hover:bg-gray-200 p-4 w-full text-error-700',
				{ 'cursor-not-allowed': isLoading }
			)}
		>
			{isLoading ? (
				<Loader />
			) : (
				<>
					<DeleteIcon className="size-6" />
					<span>{translation.deleteButton}</span>
				</>
			)}
		</button>
	);
};

const CodelistSidebarError = ({ error }: { error: 'codelist-not-found' | 'unknown' }) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	if (error === 'codelist-not-found') {
		return (
			<div className="text-red-500 text-sm font-bold text-center my-2">
				{translation.noCodelistFound}
			</div>
		);
	}

	return (
		<div className="text-red-500 text-sm font-bold text-center my-2">
			{translation.createCodelistForm.errors.unknown}
		</div>
	);
};

const CodelistSelectActionFlow = ({
	activeView,
	setActiveView,
	originalEntryId,
	projectId,
	hasCodelistWriteAccess,
	onOpenSearchModal
}: {
	activeView: 'selection' | 'create';
	setActiveView: (view: 'selection' | 'create') => void;
	originalEntryId: string;
	projectId: string;
	hasCodelistWriteAccess: boolean;
	onOpenSearchModal: () => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	if (activeView === 'create') {
		return (
			<CreateCodelistForm
				originalEntryId={originalEntryId}
				projectId={projectId}
				onCancelClicked={() => setActiveView('selection')}
			/>
		);
	}

	return (
		<div className="flex flex-col gap-6">
			<p className="text-base font-semibold text-gray-600">
				{translation.selectionView.description}
			</p>

			<div className="flex flex-col gap-4">
				{hasCodelistWriteAccess && (
					<Button
						onClick={() => setActiveView('create')}
						text={translation.selectionView.createNewButton}
						variant="primary"
						Icon={<Plus className="size-5" />}
					/>
				)}

				<Button
					variant="secondary"
					text={translation.selectionView.searchExistingButton}
					Icon={<Search className="size-5" />}
					onClick={onOpenSearchModal}
				/>
			</div>
		</div>
	);
};

const SelectedRecordView = ({
	record,
	onBack,
	originalEntryId,
	projectId
}: {
	record: CodelistRecord & { id: string };
	onBack: () => void;
	originalEntryId: string;
	projectId: string;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const attachCodelistMutation = useAttachCodelistRecordToEntryMutation({
		originalEntryId,
		projectId,
		codelistRecordId: record.id
	});

	const handleOnSave = async () => {
		try {
			await attachCodelistMutation.mutateAsync();
			onBack();
		} catch (error) {
			toast.error(translation.attachCodelist.error);

			console.error('Failed to attach codelist record:', error);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<h3 className="text-lg font-semibold">Selected Record</h3>

			<CodelistDisplay codelist={record} />

			<div className="flex flex-col gap-4 mt-6">
				<Button
					type="button"
					variant="primary"
					text="Save"
					onClick={handleOnSave}
					loading={attachCodelistMutation.isLoading}
				/>
				<Button type="button" variant="secondary" text="Back" onClick={onBack} />
			</div>
		</div>
	);
};

const LoadingView = () => {
	return (
		<div className="flex flex-col gap-4" role="progressbar">
			<CodelistEntrySkeleton />
			<CodelistEntrySkeleton />
			<CodelistEntrySkeleton />
			<CodelistEntrySkeleton />
			<CodelistEntrySkeleton />
			<CodelistEntrySkeleton />
		</div>
	);
};

const CodelistEntrySkeleton = () => (
	<div className="flex flex-row gap-2 items-center">
		<Skeleton className="size-[24px] rounded-md" />
		<Skeleton className="w-full h-[21px] rounded-md" />
	</div>
);

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Codelist',

		submitButton: 'Save',
		cancelButton: 'Cancel',
		editButton: 'Edit',
		deleteButton: 'Delete',
		noCodelistFound: 'No codelist found for this entry',

		createCodelistForm: {
			errors: {
				generic: 'Could not create codelist',
				alreadyExists: 'A codelist with this ID already exists',
				unknown: 'An error occurred while getting the codelist. Please try again later.'
			}
		},

		attachCodelist: {
			error: 'An error occurred while saving codelist'
		},

		detachCodelist: {
			error: 'An error occurred while removing codelist'
		},

		errorLoadingCodelist: {
			description: 'Could not load codelist',
			retryButton: 'Retry'
		},

		selectionView: {
			description: 'This entry has no associated codelist record. You can:',
			createNewButton: 'Create new record',
			searchExistingButton: 'Search existing records'
		}
	},
	[LanguageType.Norwegian]: {
		title: 'Kodeliste',

		submitButton: 'Lagre',
		cancelButton: 'Avbryt',
		editButton: 'Rediger',
		deleteButton: 'Slett',
		noCodelistFound: 'Ingen kodeliste funnet for denne oppføringen',

		createCodelistForm: {
			errors: {
				generic: 'Kunne ikke opprette kodeliste',
				alreadyExists: 'En kodeliste med denne ID-en finnes allerede',
				unknown: 'En feil oppstod ved henting av kodelisten. Vennligst prøv igjen senere.'
			}
		},

		attachCodelist: {
			error: 'En feil oppstod ved lagring av kodeliste'
		},

		detachCodelist: {
			error: 'En feil oppstod ved fjerning av kodeliste'
		},

		errorLoadingCodelist: {
			description: 'Kunne ikke laste kodeliste',
			retryButton: 'Last på nytt'
		},

		selectionView: {
			description: 'Denne oppføringen har ingen tilknyttet kodeliste-post. Du kan:',
			createNewButton: 'Opprett ny',
			searchExistingButton: 'Søk i eksisterende'
		}
	}
} as const;
